import React, { useEffect, useState, useCallback, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Globe2, Map, Layers, BookOpen, Users, LogOut, Settings,
  CalendarRange, Network, FileSpreadsheet, ScrollText, Bell, History,
} from "lucide-react";

const nav = [
  { key: "overview", to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { key: "regions", to: "/regions", label: "Regions", icon: Globe2 },
  { key: "projects", to: "/projects", label: "Project Progress", icon: Layers },
  { key: "languages", to: "/languages", label: "Languages", icon: BookOpen },
  { key: "notifications", to: "/notifications", label: "Notifications", icon: Bell, showBadge: true },
  { key: "users", to: "/users", label: "Users", icon: Users, adminOnly: true },
  { key: "audit", to: "/audit", label: "Audit Log", icon: ScrollText, adminOnly: true },
  { key: "annot-audit", to: "/audit/annotations", label: "Annotations Trail", icon: History, adminOnly: true },
  { key: "books", to: "/books", label: "Options", icon: BookOpen, adminOnly: true },
  { key: "kairos", to: "/kairos-sync", label: "Upload and Download", icon: FileSpreadsheet, adminOnly: true },
];

/* ---------- Notifications context (unread count + polling) ---------- */
const NotificationsCtx = createContext({ unread: 0, refresh: () => {}, markRead: async () => {} });
export const useNotifications = () => useContext(NotificationsCtx);

function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data?.unread || 0);
    } catch { /* silent */ }
  }, [user]);

  const markRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-read");
      setUnread(0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!user) return;
    refresh();
    const t = setInterval(refresh, 60_000); // poll every 60s
    return () => clearInterval(t);
  }, [user, refresh]);

  return (
    <NotificationsCtx.Provider value={{ unread, refresh, markRead }}>
      {children}
    </NotificationsCtx.Provider>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  return (
    <NotificationsProvider>
      <div className="min-h-screen flex" data-testid="app-layout">
        <aside className="w-64 bg-[color:var(--surface)] border-r border-[color:var(--border)] flex flex-col">
          <div className="px-6 py-6 border-b border-[color:var(--border)] flex items-center justify-center">
            <img
              src="/insight-logo.png"
              alt="Insight"
              className="h-8 w-auto object-contain"
              data-testid="sidebar-logo"
            />
          </div>

          <nav className="flex-1 py-4">
            {nav
              .filter((item) => {
                if (item.adminOnly) return user?.role === "admin";
                if (user?.role === "admin") return true;
                const perms = user?.menu_permissions || [];
                return perms.includes(item.key);
              })
              .map((item) => (
                <NavItem key={item.to} item={item} />
              ))}
          </nav>
        </aside>

        <main className="flex-1 min-w-0 relative">
          {/* Top-right account bar (fixed so it stays visible on scroll) */}
          <div
            className="fixed top-4 right-6 z-40 flex items-center gap-3 bg-white/95 backdrop-blur border border-[color:var(--border)] shadow-sm px-3 py-2"
            data-testid="top-account-bar"
          >
            <div className="text-right leading-tight">
              <div className="text-xs font-medium truncate max-w-[10rem]" data-testid="user-name" title={user?.email}>
                {user?.name}
              </div>
              <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">
                {user?.role?.replace("_", " ")}
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-[color:var(--border)] hover:border-[color:var(--ink)] hover:bg-[color:var(--surface-2)] transition"
              data-testid="logout-button"
              title="Sign out"
            >
              <LogOut size={13} />
              <span>Sign out</span>
            </button>
          </div>

          <Outlet />
        </main>
      </div>
    </NotificationsProvider>
  );
}

function NavItem({ item }) {
  const Icon = item.icon;
  const { unread } = useNotifications();
  const showBadge = item.showBadge && unread > 0;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="relative inline-flex items-center justify-center">
        <Icon size={16} strokeWidth={1.75} />
        {showBadge && (
          <span
            className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[color:var(--behind)] text-white text-[9px] font-bold flex items-center justify-center leading-none mono"
            data-testid="nav-notifications-badge"
            title={`${unread} new ${unread === 1 ? "entry" : "entries"}`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}
